/**
 * @file TranscriptBubbleTurn.tsx
 * @brief One transcript turn: avatar in a ring + outline bubble (mobile look).
 *
 * @details
 * Web version of the mobile turn row (MeetingTranscriptLiteRow /
 * SessionTranscriptBubbleRow, DES 27–28-09-2026): speaker A on the left in
 * brand, everyone else on the right in grey; avatar bottom-aligned beside the
 * tail and dot; name in the top break and time in the bottom break, both
 * dimmed well back (opacity 0.32) so they don't read as part of the text.
 *
 * @references
 * - components/ui/OutlineBubble.tsx
 *
 * @author DES
 * @date 30-09-2026
 */

import type { ReactNode } from "react";
import { Avatar } from "@/components/ui/Avatar";
import { AvatarRing, OutlineBubble } from "@/components/ui/OutlineBubble";
import {
  BUBBLE_TONE_CLASS,
  ringAngleToDot,
} from "@/components/ui/outlineBubbleGeometry";
import { cn } from "@/shared/theme/cn";

/** Avatar 28 in a 38 ring, 8px from the bubble — the mobile numbers. */
const RING_BOX = 38;
const ROW_GAP = 8;
const RING_GAP_ANGLE = ringAngleToDot(RING_BOX, ROW_GAP);

export function TranscriptBubbleTurn({
  isA,
  name,
  avatarUrl,
  initials,
  gender,
  meta,
  bottomAccessory,
  children,
}: {
  /** Speaker A: left + brand. Everyone else: right + grey. */
  isA: boolean;
  name: string;
  avatarUrl?: string | null;
  initials: string;
  gender?: string | null;
  /** Bottom-break text: the time (and anything that rides with it). */
  meta?: string | null;
  /**
   * (01-10-2026, live interpreter) Leads the bottom break, before `meta`: the
   * phone's 22px play button (SessionTranscriptBubbleRow `metaRow`).
   */
  bottomAccessory?: ReactNode;
  children: ReactNode;
}) {
  const side = isA ? "start" : "end";
  return (
    <li
      className={cn(
        "flex w-full items-end",
        !isA && "flex-row-reverse",
        isA ? BUBBLE_TONE_CLASS.a : BUBBLE_TONE_CLASS.other,
      )}
      style={{ gap: ROW_GAP }}
    >
      <AvatarRing size={RING_BOX} side={side} gapAngle={RING_GAP_ANGLE}>
        <Avatar
          src={avatarUrl}
          gender={gender}
          initials={initials}
          alt=""
          size="sm"
          tone="neutral"
          className={
            avatarUrl
              ? undefined
              : "bg-foreground text-background dark:bg-neutral-700 dark:text-white"
          }
        />
      </AvatarRing>
      <div
        className={cn(
          "flex min-w-0 flex-1",
          isA ? "justify-start" : "justify-end",
        )}
      >
        {/* Capped on wide screens so a bubble stays a readable line length. */}
        <div className="min-w-0 max-w-[44rem]">
          <OutlineBubble
            side={side}
            className="px-4 py-5"
            topLabel={
              <span className="max-w-[220px] truncate text-sm font-semibold text-foreground opacity-[0.32]">
                {name}
              </span>
            }
            bottomLabel={
              bottomAccessory ? (
                <span className="inline-flex items-center gap-2">
                  {bottomAccessory}
                  {meta ? (
                    <span className="text-xs font-medium tabular-nums text-muted-foreground opacity-[0.32]">
                      {meta}
                    </span>
                  ) : null}
                </span>
              ) : meta ? (
                <span className="text-xs font-medium tabular-nums text-muted-foreground opacity-[0.32]">
                  {meta}
                </span>
              ) : undefined
            }
          >
            {children}
          </OutlineBubble>
        </div>
      </div>
    </li>
  );
}
